import { createApi } from '@reduxjs/toolkit/query/react';

import { URL } from 'api/constants';
import { addParamsToUrl } from 'utils/query';
import { User, Role, BaseResponse, PaginationResponse } from 'types';
import {
  providesList,
  invalidatesList,
  cacheByIdArgProperty,
  cacheListAndByIdArgProperty,
} from 'utils/query-cache';

import { apiQuery } from '../../query';

import {
  UserImport,
  UserMfaStatus,
  UserListRequest,
  CreateManagedUserInput,
  UserProfileFormResponse,
  MessageOrgAdminsRequest,
  MessageOrgAdminsResponse,
  UpdateUserProfileFormRequest,
} from './types';

const tagType = 'USER';
const profileFormTagType = 'USER_PROFILE_FORM';
const importTagType = 'USER_IMPORT';

export const userApi = createApi({
  baseQuery: apiQuery,
  reducerPath: 'userApi',
  tagTypes: [tagType, profileFormTagType, importTagType],
  endpoints: (build) => ({
    createManagedUser: build.mutation<User, CreateManagedUserInput>({
      invalidatesTags: invalidatesList(tagType),
      query: (data) => ({ data, method: 'post', url: URL.USER_CREATE }),
      transformResponse: (response: BaseResponse<User>) => response.data,
    }),
    // Keep import details scoped to the current tenant cache as well.
    userImportDetails: build.query<
      UserImport,
      { id: string; tenantId: string }
    >({
      transformResponse: (response: BaseResponse<UserImport>) => response.data,
      query: ({ id }) => ({
        method: 'get',
        url: `${URL.USER_IMPORTS}/${id}`,
      }),
    }),
    details: build.query<User, { id: string }>({
      providesTags: cacheByIdArgProperty(tagType),
      transformResponse: (response: BaseResponse<User>) => response.data,
      query: ({ id }) => ({
        method: 'get',
        url: URL.USER_DETAILS.replace(':id', id),
      }),
    }),
    delete: build.mutation<void, { id: string; hardDelete?: boolean }>({
      invalidatesTags: invalidatesList(tagType),
      query: ({ id, hardDelete = false }) => ({
        method: 'delete',
        data: { hardDelete },
        url: URL.USER_DETAILS.replace(':id', id),
      }),
    }),
    resetAuthAppMfa: build.mutation<void, { id: string }>({
      invalidatesTags: (_result, _error, { id }) => [
        { type: tagType, id: `MFA-${id}` },
      ],
      query: ({ id }) => ({
        method: 'delete',
        url: URL.USER_RESET_AUTH_APP_MFA.replace(':id', id),
      }),
    }),
    updateProfileForm: build.mutation<void, UpdateUserProfileFormRequest>({
      invalidatesTags: cacheListAndByIdArgProperty(profileFormTagType),
      query: ({ id, data }) => ({
        data: { data },
        method: 'patch',
        url: URL.USER_PROFILE_FORM.replace(':id', id),
      }),
    }),
    list: build.query<PaginationResponse<User>, UserListRequest>({
      providesTags: providesList(tagType),
      transformResponse: (response: BaseResponse<PaginationResponse<User>>) =>
        response.data,
      query: (params) => ({
        method: 'get',
        url: addParamsToUrl(URL.USER_LIST, params),
      }),
    }),
    messageOrgAdmins: build.mutation<
      MessageOrgAdminsResponse,
      MessageOrgAdminsRequest
    >({
      transformResponse: (response: BaseResponse<MessageOrgAdminsResponse>) =>
        response.data,
      query: (data) => ({
        data,
        method: 'post',
        url: URL.USER_MESSAGE_ORG_ADMINS,
      }),
    }),
    // Tenant ID isolates the cache; the authenticated tenant scope supplies the API tenant.
    userImports: build.query<UserImport[], { tenantId: string }>({
      query: () => ({ method: 'get', url: URL.USER_IMPORTS }),
      transformResponse: (response: BaseResponse<UserImport[]>) =>
        response.data,
      providesTags: (_result, _error, { tenantId }) => [
        { id: tenantId, type: importTagType },
      ],
    }),

    userImport: build.mutation<{ importId: string }, FormData>({
      invalidatesTags: (result) => (result ? [importTagType] : []),
      transformResponse: (response: BaseResponse<{ importId: string }>) =>
        response.data,
      query: (data) => ({
        data,
        method: 'post',
        url: URL.USER_IMPORTS,
      }),
    }),
    mfaStatus: build.query<UserMfaStatus, { id: string }>({
      transformResponse: (response: BaseResponse<UserMfaStatus>) =>
        response.data,
      providesTags: (_result, _error, { id }) => [
        { type: tagType, id: `MFA-${id}` },
      ],
      query: ({ id }) => ({
        method: 'get',
        url: URL.USER_MFA_STATUS.replace(':id', id),
      }),
    }),
    updateTenants: build.mutation<User, Pick<User, 'id' | 'tenantIds'>>({
      invalidatesTags: cacheListAndByIdArgProperty(tagType),
      transformResponse: (response: BaseResponse<User>) => response.data,
      query: ({ id, tenantIds }) => ({
        method: 'patch',
        data: { tenantIds },
        url: URL.USER_CHANGE_TENANTS.replace(':id', id),
      }),
    }),
    updateProjects: build.mutation<User, Pick<User, 'id' | 'projectIds'>>({
      invalidatesTags: cacheListAndByIdArgProperty(tagType),
      transformResponse: (response: BaseResponse<User>) => response.data,
      query: ({ id, projectIds }) => ({
        method: 'patch',
        data: { projectIds },
        url: URL.USER_CHANGE_PROJECTS.replace(':id', id),
      }),
    }),
    profileForm: build.query<UserProfileFormResponse['data'], { id: string }>({
      providesTags: cacheByIdArgProperty(profileFormTagType),
      transformResponse: (response: UserProfileFormResponse) => response.data,
      query: ({ id }) => ({
        method: 'get',
        headers: { 'allow-not-found': true },
        url: URL.USER_PROFILE_FORM.replace(':id', id),
      }),
    }),
    updateManagedUser: build.mutation<
      User,
      Omit<CreateManagedUserInput, 'email' | 'guestId'> & { id: string }
    >({
      invalidatesTags: cacheListAndByIdArgProperty(tagType),
      transformResponse: (response: BaseResponse<User>) => response.data,
      query: ({ id, ...data }) => ({
        data,
        method: 'patch',
        url: URL.USER_DETAILS.replace(':id', id),
      }),
    }),
    updateDemo: build.mutation<User, Pick<User, 'id' | 'isDemo'>>({
      invalidatesTags: cacheListAndByIdArgProperty(tagType),
      transformResponse: (response: BaseResponse<User>) => response.data,
      query: (user) => {
        const { id, isDemo } = user;
        return {
          method: 'patch',
          data: { isDemo },
          url: URL.USER_CHANGE_DEMO.replace(':id', id),
        };
      },
    }),
    updateStatus: build.mutation<User, Pick<User, 'id' | 'isActive'>>({
      invalidatesTags: cacheListAndByIdArgProperty(tagType),
      transformResponse: (response: BaseResponse<User>) => response.data,
      query: (user) => {
        const { id, isActive } = user;
        return {
          method: 'patch',
          data: { isActive },
          url: URL.USER_CHANGE_STATUS.replace(':id', id),
        };
      },
    }),
    updateHealthDepartmentScopes: build.mutation<
      User,
      { id: string; healthDepartmentIds: string[] }
    >({
      invalidatesTags: cacheListAndByIdArgProperty(tagType),
      transformResponse: (response: BaseResponse<User>) => response.data,
      query: ({ id, healthDepartmentIds }) => ({
        method: 'patch',
        data: { healthDepartmentIds },
        url: URL.USER_CHANGE_HEALTH_DEPARTMENT_SCOPES.replace(':id', id),
      }),
    }),
    update: build.mutation<
      User,
      Pick<User, 'id'> &
        Partial<
          Pick<
            User,
            | 'role'
            | 'isDemo'
            | 'lastName'
            | 'isActive'
            | 'firstName'
            | 'isLibraryAccessEnabled'
          >
        >
    >({
      invalidatesTags: cacheListAndByIdArgProperty(tagType),
      transformResponse: (response: BaseResponse<User>) => response.data,
      query: ({ id, role, ...rest }) => {
        const payload: Record<string, any> = {
          ...rest,
          role,
        };
        if (role !== Role.TENANT_ADMIN) {
          delete payload.isLibraryAccessEnabled;
        }
        return {
          data: payload,
          method: 'patch',
          url: URL.USER_DETAILS.replace(':id', id),
        };
      },
    }),
    create: build.mutation<
      User,
      Omit<User, 'id'> & { healthDepartmentIds?: string[] }
    >({
      invalidatesTags: invalidatesList(tagType),
      transformResponse: (response: BaseResponse<User>) => response.data,
      query: (data) => {
        const {
          role,
          tenantIds,
          projectIds,
          healthDepartmentIds,
          isLibraryAccessEnabled,
          ...rest
        } = data;
        const payload: Record<string, any> = {
          ...rest,
          role,
        };
        if (!payload.phone) {
          delete payload.phone;
        }
        if (role === Role.PROJECT_ADMIN) {
          payload.projectIds = projectIds;
        } else if (role === Role.LHD_MIVAX_ADMIN) {
          payload.projectIds = projectIds;
          payload.healthDepartmentIds = healthDepartmentIds;
        } else if (role === Role.TENANT_ADMIN) {
          payload.tenantIds = tenantIds;
          payload.isLibraryAccessEnabled = Boolean(isLibraryAccessEnabled);
        } else if (
          role === Role.CONTENT_MANAGER ||
          role === Role.LIBRARY_ADMIN ||
          role === Role.REPORT_MANAGER
        ) {
          payload.tenantIds = tenantIds;
        }
        return {
          data: payload,
          method: 'post',
          url: URL.USER_CREATE,
        };
      },
    }),
  }),
});

export const {
  useListQuery,
  useDetailsQuery,
  useLazyListQuery,
  useMfaStatusQuery,
  useDeleteMutation,
  useCreateMutation,
  useUpdateMutation,
  useProfileFormQuery,
  useLazyDetailsQuery,
  useUserImportsQuery,
  useUpdateDemoMutation,
  useUserImportMutation,
  useUpdateStatusMutation,
  useUpdateTenantsMutation,
  useUpdateProjectsMutation,
  useUserImportDetailsQuery,
  useResetAuthAppMfaMutation,
  useMessageOrgAdminsMutation,
  useCreateManagedUserMutation,
  useUpdateManagedUserMutation,
  useUpdateProfileFormMutation,
  useUpdateHealthDepartmentScopesMutation,
} = userApi;
